import { notFound } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { listPublicExperiences } from "../../../api/src/experiences/service";
import { readCollection, readContentItem, readLibrary, readPostsFeed } from "./content.server";
import { readDocumentPage } from "./document.server";
import { siteContext } from "./site.server";

const nativeOnly = (input: unknown) => input === true;

/**
 * News (/blog) in one request: the page the creator composed for it, or the native feed. A
 * search or filter always asks for the feed. A feed loaded for the bare address of a site without
 * a composed page answers every view of /blog (`everyView`), so the page can filter it in place.
 */
export const loadNews = createServerFn({ method: "GET" })
  .validator(nativeOnly)
  .handler(async ({ data: native, context }) => {
    const drate = await siteContext(context);
    const experience = (await listPublicExperiences(drate)).find((item) => item.kind === "blog");
    if (!experience) return { kind: "missing" as const };
    if (!native) {
      const page = await readDocumentPage(drate, "/blog");
      if (page.kind === "page" || page.kind === "redirect") return page;
    }
    return { ...(await readPostsFeed(drate, experience)), everyView: !native };
  });

/**
 * The Library (/library) in one request: the composed page, or the native library; like News, a
 * library loaded for a site without a composed page answers every view of /library.
 */
export const loadLibrary = createServerFn({ method: "GET" })
  .validator(nativeOnly)
  .handler(async ({ data: native, context }) => {
    const drate = await siteContext(context);
    const experience = (await listPublicExperiences(drate)).find((item) => item.kind === "library");
    if (!experience) return { kind: "missing" as const };
    if (!native) {
      const page = await readDocumentPage(drate, "/library");
      if (page.kind === "page" || page.kind === "redirect") return page;
    }
    return { ...(await readLibrary(drate, experience)), everyView: !native };
  });

/** One post (/blog/:slug) or library item (/library/:slug), authorized for this visitor. */
export const loadContentItem = createServerFn({ method: "GET" })
  .validator((input: unknown) => {
    const { slug, section } = (input ?? {}) as { slug?: unknown; section?: unknown };
    if (typeof slug !== "string" || !slug || slug.length > 200) throw notFound();
    if (section !== "blog" && section !== "library") throw notFound();
    return { slug, section: section as "blog" | "library" };
  })
  .handler(async ({ data, context }) =>
    readContentItem(await siteContext(context), data.slug, data.section),
  );

/** A collection or course (/collections/:slug). */
export const loadCollection = createServerFn({ method: "GET" })
  .validator((slug: unknown) => {
    if (typeof slug !== "string" || !slug || slug.length > 200) throw notFound();
    return slug;
  })
  .handler(async ({ data: slug, context }) => readCollection(await siteContext(context), slug));
